<template>
  <form @submit.prevent="salvar" class="formas-pagamento-form">
    <p class="field-hint">{{ t('Campo em branco herda o padrão da forma de pagamento.') }}</p>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_aceita_parcelado">{{ t('Aceita parcelar?') }}</label>
          <select-list
            elem-id="fpf_aceita_parcelado"
            v-model="formData.fpf_aceita_parcelado"
            :itens-select="aceitaOptions"
            :auto-select-first="false"
            :placeholder="aceitaOptions[0].Name"
            data-testid="fpf-select-aceita-parcelado"
          />
        </div>
      </div>
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_parcelas_max">{{ t('Máximo de parcelas') }}</label>
          <input-number
            elem-id="fpf_parcelas_max"
            v-model="formData.fpf_parcelas_max"
            :placeholder="String(item.forma_pagamento_parcelas_max ?? PARCELAS_MAX_PADRAO)"
            :min="1"
            :max="48"
            data-testid="fpf-input-parcelas-max"
          />
        </div>
      </div>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_dias_para_receber">{{ t('Dias para receber') }}</label>
          <input-number
            elem-id="fpf_dias_para_receber"
            v-model="formData.fpf_dias_para_receber"
            :placeholder="String(item.forma_pagamento_dias_para_receber)"
            :min="0"
            :max="365"
            data-testid="fpf-input-dias-para-receber"
          />
        </div>
      </div>
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_possui_taxas">{{ t('Possui taxas?') }}</label>
          <select-list
            elem-id="fpf_possui_taxas"
            v-model="formData.fpf_possui_taxas"
            :itens-select="possuiTaxasOptions"
            :auto-select-first="false"
            :placeholder="possuiTaxasOptions[0].Name"
            data-testid="fpf-select-possui-taxas"
          />
        </div>
      </div>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_taxa_mensal">{{ t('Taxa mensal') }} (%)</label>
          <input-number
            elem-id="fpf_taxa_mensal"
            v-model="formData.fpf_taxa_mensal"
            :placeholder="String(item.forma_pagamento_taxa_mensal)"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :min="0"
            data-testid="fpf-input-taxa-mensal"
          />
        </div>
      </div>
      <div class="flex-item-half">
        <div class="form-group">
          <label for="fpf_taxa_transacao">{{ t('Taxa por transação') }}</label>
          <input-number
            elem-id="fpf_taxa_transacao"
            v-model="formData.fpf_taxa_transacao"
            :placeholder="String(item.forma_pagamento_taxa_transacao ?? 0)"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :min="0"
            data-testid="fpf-input-taxa-transacao"
          />
        </div>
      </div>
    </div>

    <div class="flex-container">
      <div class="flex-item-full">
        <div class="form-group">
          <label for="fpf_taxa_transacao_tipo">{{ t('Tipo da taxa de transação') }}</label>
          <select-list
            elem-id="fpf_taxa_transacao_tipo"
            v-model="formData.fpf_taxa_transacao_tipo"
            :itens-select="tipoTaxaOptions"
            :auto-select-first="false"
            :placeholder="tipoTaxaOptions[0].Name"
            data-testid="fpf-select-tipo-taxa"
          />
        </div>
      </div>
    </div>

    <small class="field-hint">{{ t('As taxas ficam só registradas: nenhum cálculo usa estes valores. A taxa de cada recebimento continua informada nele ou vinda do gateway.') }}</small>

    <div v-if="erro" class="alert alert-danger" data-testid="fpf-erro">{{ erro }}</div>
  </form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputNumber from '../../inputs/InputNumber.vue';
import SelectList from '../../inputs/SelectList.vue';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { PARCELAS_MAX_PADRAO } from '../../composables/useParcelamento';
import { log } from '../../utils/DebugLogger';
import type { FormasPagamentoFranquia_ItemRow } from '../../contracts/FormasPagamentoFranquia_ItemRow';

interface SelectOption {
  ID: string;
  Name: string;
}

const props = defineProps<{
  item: FormasPagamentoFranquia_ItemRow;
  franquiaId: number;
}>();

const emit = defineEmits<{
  'save-success': [];
  'validation-failed': [];
}>();

const { t } = useI18n();
const { salvarAplicacao } = useFormasPagamento();

/** Vazio nos selects = herda; os números são null. Campo que não vai no corpo volta a herdar. */
const textoOuVazio = (valor: number | null | undefined): string => (valor === null || valor === undefined ? '' : String(valor));
const numeroOuHerda = (valor: string | number): number | undefined => (valor === '' ? undefined : Number(valor));

const formData = ref({
  fpf_aceita_parcelado: textoOuVazio(props.item.fpf_aceita_parcelado),
  fpf_possui_taxas: textoOuVazio(props.item.fpf_possui_taxas),
  fpf_taxa_transacao_tipo: textoOuVazio(props.item.fpf_taxa_transacao_tipo),
  fpf_parcelas_max: props.item.fpf_parcelas_max ?? null,
  fpf_dias_para_receber: props.item.fpf_dias_para_receber ?? null,
  fpf_taxa_mensal: props.item.fpf_taxa_mensal ?? null,
  fpf_taxa_transacao: props.item.fpf_taxa_transacao ?? null
});
const erro = ref<string>('');

const herdar = (rotulo: string | undefined): SelectOption => ({ ID: '', Name: `${t('Padrão')}: ${rotulo ?? '—'}` });

const opcoesAceita = computed<SelectOption[]>(() => [
  { ID: '0', Name: t('Não') },
  { ID: '1', Name: t('Sim') }
]);
const aceitaOptions = computed<SelectOption[]>(() => [
  herdar(opcoesAceita.value.find((o) => o.ID === String(props.item.forma_pagamento_aceita_parcelado))?.Name),
  ...opcoesAceita.value
]);

const opcoesPossuiTaxas = computed<SelectOption[]>(() => [
  { ID: '0', Name: t('Não') },
  { ID: '1', Name: t('Sim - mensal') },
  { ID: '2', Name: t('Sim - por transação') },
  { ID: '3', Name: t('Sim - mensal e por transação') }
]);
const possuiTaxasOptions = computed<SelectOption[]>(() => [
  herdar(opcoesPossuiTaxas.value.find((o) => o.ID === String(props.item.forma_pagamento_possui_taxas))?.Name),
  ...opcoesPossuiTaxas.value
]);

const opcoesTipoTaxa = computed<SelectOption[]>(() => [
  { ID: '1', Name: t('Percentual') },
  { ID: '2', Name: t('Fixo por transação') }
]);
const tipoTaxaOptions = computed<SelectOption[]>(() => [
  herdar(opcoesTipoTaxa.value.find((o) => o.ID === String(props.item.forma_pagamento_taxa_transacao_tipo))?.Name),
  ...opcoesTipoTaxa.value
]);

const validar = (): string => {
  const { fpf_parcelas_max: parcelas, fpf_dias_para_receber: dias, fpf_taxa_mensal: mensal, fpf_taxa_transacao: transacao } = formData.value;
  if (parcelas !== null && (parcelas < 1 || parcelas > 48)) return t('O máximo de parcelas deve estar entre 1 e 48.');
  if (dias !== null && (dias < 0 || dias > 365)) return t('Dias para receber deve estar entre 0 e 365.');
  if ((mensal !== null && mensal < 0) || (transacao !== null && transacao < 0)) return t('A taxa não pode ser negativa.');
  return '';
};

const salvar = async (): Promise<void> => {
  erro.value = validar();
  if (erro.value) {
    emit('validation-failed');
    return;
  }
  try {
    await salvarAplicacao({
      forma_pagamento_id: props.item.forma_pagamento_id,
      franquia_id: props.franquiaId,
      fpf_aceita_parcelado: numeroOuHerda(formData.value.fpf_aceita_parcelado),
      fpf_possui_taxas: numeroOuHerda(formData.value.fpf_possui_taxas),
      fpf_taxa_transacao_tipo: numeroOuHerda(formData.value.fpf_taxa_transacao_tipo),
      fpf_parcelas_max: formData.value.fpf_parcelas_max ?? undefined,
      fpf_dias_para_receber: formData.value.fpf_dias_para_receber ?? undefined,
      fpf_taxa_mensal: formData.value.fpf_taxa_mensal ?? undefined,
      fpf_taxa_transacao: formData.value.fpf_taxa_transacao ?? undefined
    });
    emit('save-success');
  } catch (err) {
    log('FormasPagamentoFranquiaForm', 'salvar', 'Error', err);
    erro.value = err instanceof Error ? err.message : t('Erro ao salvar');
  }
};

defineExpose({ salvar });
</script>

<style scoped>
.field-hint {
  display: block;
  margin-bottom: 12px;
  color: var(--text-color-secondary);
}
</style>
